import { supabase } from './supabase';

function mapDocumentRow(row) {
  if (!row) return null;
  return {
    ...row,
    type: row.document_type,
    user_id: row.client_id,
  };
}

function mapClientProfile(p) {
  if (!p) return null;
  return {
    ...p,
    name: p.full_name ?? '',
  };
}

/** Map DB voucher row for UI that expects discount_type + value */
export function enrichVoucherForUi(row) {
  if (!row) return null;
  const pct = row.discount_percent;
  return {
    ...row,
    discount_type: pct >= 100 ? 'full' : 'percent',
    value: pct,
  };
}

export async function getClientDocuments(clientId) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapDocumentRow);
  } catch (e) {
    console.error('getClientDocuments', e);
    throw e;
  }
}

export async function getDocumentById(documentId) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .maybeSingle();
    if (error) throw error;
    return mapDocumentRow(data);
  } catch (e) {
    console.error('getDocumentById', e);
    throw e;
  }
}

export async function createDocument(clientId, documentType) {
  try {
    const { data: prof, error: pErr } = await supabase
      .from('profiles')
      .select('assigned_advisor_id')
      .eq('id', clientId)
      .maybeSingle();
    if (pErr) console.error('createDocument profile lookup', pErr);

    const { data, error } = await supabase
      .from('documents')
      .insert({
        client_id: clientId,
        document_type: documentType,
        status: 'draft',
        form_data: {},
        advisor_id: prof?.assigned_advisor_id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapDocumentRow(data);
  } catch (e) {
    console.error('createDocument', e);
    throw e;
  }
}

export async function updateDocumentFormData(documentId, formData) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .update({
        form_data: formData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId)
      .select()
      .single();
    if (error) throw error;
    return mapDocumentRow(data);
  } catch (e) {
    console.error('updateDocumentFormData', e);
    throw e;
  }
}

export async function submitDocumentForReview(documentId) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .update({
        status: 'in_review',
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId)
      .select()
      .single();
    if (error) throw error;
    return mapDocumentRow(data);
  } catch (e) {
    console.error('submitDocumentForReview', e);
    throw e;
  }
}

export async function finalizeDocument(documentId, publitioFileId, publitioFileUrl) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .update({
        status: 'finalized',
        publitio_file_id: publitioFileId,
        publitio_file_url: publitioFileUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId)
      .select()
      .single();
    if (error) throw error;
    return mapDocumentRow(data);
  } catch (e) {
    console.error('finalizeDocument', e);
    throw e;
  }
}

/** Remove a draft document (DB has no discarded status) */
export async function deleteDocument(documentId) {
  try {
    const { error } = await supabase.from('documents').delete().eq('id', documentId);
    if (error) throw error;
  } catch (e) {
    console.error('deleteDocument', e);
    throw e;
  }
}

export async function getAdvisorDocuments(advisorId) {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('advisor_id', advisorId)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapDocumentRow);
  } catch (e) {
    console.error('getAdvisorDocuments', e);
    throw e;
  }
}

export async function getAdvisorClients(advisorId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('assigned_advisor_id', advisorId)
      .eq('role', 'client');
    if (error) throw error;
    return (data ?? []).map(mapClientProfile);
  } catch (e) {
    console.error('getAdvisorClients', e);
    throw e;
  }
}

export async function getProfileById(profileId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', profileId)
      .maybeSingle();
    if (error) throw error;
    return mapClientProfile(data);
  } catch (e) {
    console.error('getProfileById', e);
    throw e;
  }
}

export async function validateVoucher(code) {
  try {
    const normalized = String(code ?? '').trim().toUpperCase();
    if (!normalized) return { valid: false, reason: 'invalid' };

    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .eq('code', normalized)
      .eq('is_active', true)
      .maybeSingle();
    if (error) throw error;
    if (!data) return { valid: false, reason: 'invalid' };
    return { valid: true, voucher: data };
  } catch (e) {
    console.error('validateVoucher', e);
    return { valid: false, reason: 'invalid' };
  }
}

export async function redeemVoucher(userId, code) {
  try {
    const validation = await validateVoucher(code);
    if (!validation.valid) {
      return { error: validation.reason === 'invalid' ? 'invalid' : 'invalid' };
    }

    const { data: profile, error: profErr } = await supabase
      .from('profiles')
      .select('id, voucher_code')
      .eq('id', userId)
      .maybeSingle();
    if (profErr) throw profErr;
    if (profile?.voucher_code) {
      return { error: 'already_redeemed' };
    }

    const normalized = validation.voucher.code;

    const { error: upProfErr } = await supabase
      .from('profiles')
      .update({
        voucher_code: normalized,
        voucher_applied_at: new Date().toISOString(),
      })
      .eq('id', userId);
    if (upProfErr) throw upProfErr;

    const { data: vRow, error: vFetchErr } = await supabase
      .from('vouchers')
      .select('times_redeemed')
      .eq('code', normalized)
      .single();
    if (vFetchErr) throw vFetchErr;

    const { error: vUpErr } = await supabase
      .from('vouchers')
      .update({ times_redeemed: (vRow?.times_redeemed ?? 0) + 1 })
      .eq('code', normalized);
    if (vUpErr) throw vUpErr;

    return { success: true, voucher: enrichVoucherForUi(validation.voucher) };
  } catch (e) {
    console.error('redeemVoucher', e);
    throw e;
  }
}

export async function getNotesByDocument(documentId) {
  try {
    const { data, error } = await supabase
      .from('advisor_notes')
      .select('*')
      .eq('document_id', documentId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  } catch (e) {
    console.error('getNotesByDocument', e);
    throw e;
  }
}

export async function saveAdvisorNote(documentId, advisorId, note) {
  try {
    const { data: existing, error: exErr } = await supabase
      .from('advisor_notes')
      .select('id')
      .eq('document_id', documentId)
      .eq('advisor_id', advisorId)
      .maybeSingle();
    if (exErr) throw exErr;

    if (existing?.id) {
      const { data, error } = await supabase
        .from('advisor_notes')
        .update({
          note,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    }

    const { data, error } = await supabase
      .from('advisor_notes')
      .insert({
        document_id: documentId,
        advisor_id: advisorId,
        note,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (e) {
    console.error('saveAdvisorNote', e);
    throw e;
  }
}
